import { useMemo, useCallback } from "react";
import { useParams } from "react-router";
import PropTypes from "prop-types";
import {
  CardView,
  Card,
  Heading,
  Content,
  ButtonGroup,
  Button,
  FileTrigger,
  DropZone,
  IllustratedMessage,
} from "@react-spectrum/s2";
import { useDragAndDrop } from "react-aria-components";

import DropToUpload from "@react-spectrum/s2/illustrations/linear/DropToUpload";

import { S3Image } from "@app/components/S3Image";
import { AssetCard } from "./AssetCard";

import { useVideo, useVideoTemplate } from "@app/hooks/videos";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

const ACCEPTED_FILE_TYPES = ["image/jpeg", "image/png", "image/svg+xml"];

export const UploadedImages = ({
  videoId,
  onDrop = () => {},
  onMenuAction,
}) => {
  const params = useParams();

  const projectId = videoId || params?.id;

  const video = useVideo(projectId);
  const template = useVideoTemplate(video.data?.templateId);
  const templateData = template.data;

  const { dragAndDropHooks } = useDragAndDrop({
    getItems: (keys, items) => {
      return items.map((item) => ({
        "image/asset": JSON.stringify(item),
      }));
    },
    renderDragPreview: (items) => {
      const [item] = items.map((it) => JSON.parse(it["image/asset"]));

      if (!item) {
        return null;
      }

      return (
        <div
          className={style({
            width: 120,
            aspectRatio: "16 / 10",
            borderRadius: "lg",
            overflow: "hidden",
            boxShadow: "dragged",
          })}
        >
          <S3Image
            filePath={item.filePath}
            src={item.src}
            alt={item.alt || item.name}
            styles={style({
              size: "full",
              objectFit: "contain",
            })}
          />
        </div>
      );
    },
  });

  const items = useMemo(
    () =>
      video.data?.assets.filter((asset) =>
        asset?.name?.startsWith("upload__"),
      ) || [],
    [video.data?.assets],
  );

  const handleDragOperation = useCallback(({ types }) => {
    if (
      Array.from(types)[0]?.startsWith("image/") &&
      !types.has("image/asset")
    ) {
      return "copy";
    }

    return "cancel";
  }, []);

  return (
    <div className={style({ paddingY: 12 })}>
      {/* <Heading styles={style({ ...headingStyle(), font: "heading-xs" })}>
        <Text>Uploaded images</Text>
        <FileTrigger
          acceptedFileTypes={ACCEPTED_FILE_TYPES}
          allowsMultiple
          onSelect={onDrop}
        >
          <ActionButton isQuiet size="S">
            <UploadToCloudIcon />
            <Text>Browse files</Text>
          </ActionButton>
        </FileTrigger>
      </Heading> */}

      <DropZone
        getDropOperation={handleDragOperation}
        onDrop={(event) => onDrop(event.items)}
        replaceMessage="Drop files to upload"
        UNSAFE_className={style({ padding: 8 })}
      >
        {items.length ? (
          <CardView
            aria-label="Uploaded images"
            textValue="Uploaded images"
            items={items}
            disabledKeys={
              new Set(
                items
                  ?.filter?.((item) => item.isDisabled)
                  .map((item) => item.id) ?? [],
              )
            }
            dragAndDropHooks={dragAndDropHooks}
            layout="grid"
            variant="quiet"
            size="S"
            styles={style({ size: "full" })}
          >
            {(item) => (
              <Card id={item.name} textValue={item.alt || item.id}>
                <AssetCard
                  item={item}
                  templateData={templateData}
                  onMenuAction={onMenuAction}
                />
              </Card>
            )}
          </CardView>
        ) : (
          <IllustratedMessage size="S">
            <DropToUpload />
            <Heading>Drag and drop your file</Heading>
            <Content>Or, select a file from your computer</Content>
            <ButtonGroup>
              <FileTrigger
                acceptedFileTypes={ACCEPTED_FILE_TYPES}
                allowsMultiple
                onSelect={onDrop}
              >
                <Button variant="secondary">Browse files</Button>
              </FileTrigger>
            </ButtonGroup>
          </IllustratedMessage>
        )}
      </DropZone>
    </div>
  );
};

UploadedImages.propTypes = {
  videoId: PropTypes.string,
  onDrop: PropTypes.func,
  onMenuAction: PropTypes.func,
};
